import Head from "next/head";
import EqualDivisionSection from "components/CLM/EqualDivisionSection";
import FAQ2 from "components/CLM/FAQ2";
import TestimonialSlider from "components/CLM/TestimonialSliderSection";
import { CLMButtonPrimary } from "components/CLM/v3/Buttons";
import AnimatedLogos from "components/customers/AnimatedLogos";
import Image from "next/image";
import { BASE_ASSETS_URL } from "constants/constants";

const templateFaqData = [
  {
    question:
      "What are BoloSign Templates and how do they help my legal or procurement team?",
    answer:
      "BoloSign Templates let you create reusable contract formats (like NDAs, MSAs, SOWs, etc.) with predefined fields, logic, and workflows. Instead of starting from scratch each time, your team can generate accurate, policy-compliant contracts in minutes—at scale.",
  },
  {
    question: "Can I convert existing documents into BoloSign templates?",
    answer:
      "Yes! You can upload any existing PDF or DOCX file and use our AI-powered builder to detect variables, clauses, and placeholders. In seconds, your document becomes a dynamic, reusable template with form logic, approval routing, and signer fields already set up.",
  },
  {
    question:
      "Can BoloSign Templates integrate with tools like Salesforce, HubSpot, or Airtable?",
    answer:
      "Yes! You can auto-fill templates using CRM data from Salesforce or HubSpot, or connect with Airtable, Google Sheets, and APIs—so your teams spend less time on manual entry and more on closing deals.",
  },
  {
    question: "How does BoloSign automate the contract creation process?",
    answer:
      "Using smart questionnaires, AI-powered field detection, and conditional logic, contracts can be generated instantly—no copy-pasting or clause hunting. It's faster, more accurate, and scalable for large teams.",
  },
  {
    question:
      "What customization features are available in BoloSign Templates?",
    answer:
      "You can apply company branding (logos, fonts, headers), control clause logic based on responses, set approval workflows, and define who can access or edit the template. It adapts to your legal and operational playbook.",
  },
  {
    question: "How does BoloSign help minimize legal risk with templates?",
    answer:
      "Once legal approves a template, it’s locked in. Every version is tracked, reused consistently, and auditable—ensuring no team ever sends out non-compliant contracts or outdated clauses again.",
  },
  {
    question: "How are templates maintained and versioned over time?",
    answer:
      "BoloSign offers centralized version control. You can update templates, track who made changes, and ensure that only the latest version is available to business teams—so outdated templates are never accidentally used.",
  },
];

const Template = () => {
  return (
    <>
      <Head>
        <title>
          Contract Templates for Legal Teams | BoloSign CLM Software
        </title>
        <meta
          name="description"
          content="BoloSign Templates help legal teams create, manage, and automate reusable contract templates with AI-powered field detection, approval workflows, and integrations."
        />
        <meta
          name="keywords"
          content="Contract templates, reusable contract templates, legal contract automation, BoloSign templates, AI contract templates, contract management software, CLM templates"
        />
        <link rel="canonical" href="https://www.bolosign.com/clm/templates/" />
      </Head>
      <section className={` py-7 md:pb-32 transition-all duration-700`}>
        <div className="max-w-screen-xl md:px-8 px-6 mx-auto grid md:grid-cols-2 gap-6 md:gap-12 items-center">
          <div className="flex flex-col gap-6 ">
            <p className="uppercase text-purple-600 mb-2 text-xs md:text-2xl font-semibold">
              TEMPLATES
            </p>
            <p className={`text-[28px] md:text-5xl font-bold text-start`}>
              One template. Unlimited use cases.
            </p>
            <p
              className={`text-gray-900 mb-6  md:text-2xl font-semibold md:font-normal font-medium text-left`}
            >
              Turn any document into a reusable template. Intake forms auto-fill
              fields like names, dates, and roles—so your team can generate
              error-free contracts in seconds.
            </p>
            <div className="">
              <CLMButtonPrimary
                buttonText="Request a Demo"
                buttonUrl="/clm/request-demo"
              />
            </div>
          </div>

          {/* Image Section */}
          <div className={`bg-color  rounded-2xl relative`}>
            <Image
              src={`${BASE_ASSETS_URL}/v2/clm/clm/templates-hero-1.png`}
              alt="Contract Creation"
              width={500}
              height={500}
              className={`w-full`}
            />
          </div>
        </div>
      </section>

      <section className="py-10 bg-dark">
        <p className="text-2xl font-semibold text-center text-white">
          Trusted By
        </p>
        <AnimatedLogos />
      </section>

      <div className="text-center pt-20 md:pt-32 max-w-screen-xl mx-auto">
        <h2 className="text-4xl md:text-6xl font-semibold mb-8 hidden md:block">
          Create smart contract templates <br />{" "}
          <span className="block md:mt-3">your whole team can use</span>
        </h2>
        <h2 className="text-4xl md:text-6xl font-semibold mb-8 block md:hidden">
          Create smart contract templates your whole team can use
        </h2>
        <p className="text-xl lg:w-[70%] xl:w-[60%] mx-auto px-6 md:px-0">
          BoloSign lets you draft, edit, and review contracts using Microsoft
          Word’s interface—plus advanced tools built for legal teams.
        </p>
      </div>

      <EqualDivisionSection
        // title=""
        heading="Convert any contract into a reusable template"
        description="Create, update, and manage reusable contract templates with version control and built-in AI support. So, your team always works with the right terms."
        checks={[
          "Create templates from existing PDFs or Word files.",
          "Get suggestions by AI for fields and variables.",
          "Maintain control with centralized access, permissions, and version history.",
        ]}
        // image={`${BASE_ASSETS_URL}/v2/clm/clm/templates-sales.gif`}
        video={`${BASE_ASSETS_URL}/v2/clm/videos/templates-sales.mp4`}
        bgColor=""
        imageRight={false}
        leftDivStyle="items-center md:-mt-24 md:-mb-32"
      />

      <EqualDivisionSection
        // title=""
        heading="Instantly customize contracts for any scenario"
        description="Smart questionnaires fill in approved templates—so every agreement is fast, accurate, and aligned with company policy."
        checks={[
          "Share an intake form to capture deal details.",
          "Answers auto-populate your approved template.",
          "Conditional logic tweaks clauses in real time.",
        ]}
        image={`${BASE_ASSETS_URL}/v2/clm/clm/templates-instantly-1.png`}
        bgColor="bg-cream"
        imageRight={true}
      />

      <EqualDivisionSection
        // title=""
        heading="Create contracts with built-in approval logic"
        description="Set up dynamic rules to route contracts to the right people—based on value, role, or risk—so approvals never hold up the deal."
        checks={[
          "Set rules to auto-route approvals by contract value, team, or risk level.",
          "Get notified instantly when stakeholder input is needed.",
          "Ensure nothing gets signed without the required internal reviews.",
        ]}
        image={`${BASE_ASSETS_URL}/v2/clm/clm/templates-approval-3.png`}
        bgColor="bg-dark"
        imageRight={false}
      />

      <EqualDivisionSection
        // title=""
        heading="Connect BoloSign to HubSpot, Pipedrive & other tools "
        description="Automate your contract workflows by syncing your favorite CRMs and tools—every agreement is accurate, policy-aligned, and effortless."
        checks={[
          "Import Deals, Contacts & custom fields automatically.",
          "Draft and dispatch contracts in one click.",
          "Route sign-offs and reminders on autopilot.",
        ]}
        image={`${BASE_ASSETS_URL}/v2/clm/clm/templates-opportunities.png`}
        bgColor=""
        imageRight={true}
        // leftDivStyle="items-center md:-mt-20"
      />

      <TestimonialSlider />

      <section className="bg-cream text-black py-16 md:px-10 px-4 xl:px-20 text-center md:pt-24 pt-12 ">
        <FAQ2 faqData={templateFaqData} heading={"FAQs"} />
      </section>
    </>
  );
};

export default Template;
